/* eslint-disable react/display-name */
import React, { useState } from "react";
import TableBase from "../../../../components/table/TableBase";
import moment from "moment";
import { DATETIME_FORMAT_FRONTEND } from "configs/constants";
import PoolStatus from "components/items/PoolStatus";
import { convertEmergencyStatus } from "helper/util";
import "modules/admin/orders/styles/routes.scss";
import { Checkbox, Tooltip, message, Button } from "antd";
import FacturaCell from "../../../../components/FacturaCell/FacturaCell";

// for button for Print Packing Slip
import generatePdfPackingSlipPool from "helper/generatePdfPackingSlipPool";
import { PrintPDFIcon } from "components/svgs";

const FlexioPoolTable = ({
  dataSource,
  pagination,
  hiddenColumns = [],
  setSelectedPackage,
  setShowUpdateStatus,
  selectAll,
  handlePackageArray,
  onCreateFactura,
  onAddToFactura,
  ...props
}) => {
  const [messageApi, contextHolder] = message.useMessage();

  // Handles the "loading" state for Printing (grey out until done)
  const [printing, setPrinting] = useState(false);

  // This function gets details and creates the PDF packing slip(s).
  // Data "dataSource" is coming from "PackageList" in FlexioPoolList.jsx; e.g. array of 20 packages.
  const handlePrintPackingSlipPool = async (packageId) => {
    // The logic that was here in the Shift version, was just preparatory for passing to generatePdfPackingSlip. No need to pre-manage the Flexio Pool Packages.

    try {
      // grey out screen for loading
      setPrinting(true);

      // calls helper/generatePdfPackingSlipPool with both datasouce (array of 20 / filtered) and distinct package ID for printing single
      const doc = await generatePdfPackingSlipPool(dataSource, packageId);
      const filename = `Flexio-Pool-` + packageId?.substr(0, 8)?.toUpperCase() + `.pdf`;
      doc?.save(filename);
    } finally {
      // un-grey out screen for loading
      setPrinting(false);
    }
  };

  const columns = [
    {
      title: "Print",
      width: 50,
      fixed: "left",
      render: (record) => {
        const { id } = record;
        return (
          <Button
            size="small"
            type="link"
            onClick={() => handlePrintPackingSlipPool(id)}
            loading={printing}
            title="Print Packing Slip"
          >
            <PrintPDFIcon />
          </Button>
        );
      }
    },
    /* The "Select" column only shows up in the batch update modal,
    used to select a few packages for the status update*/
    {
      title: "Select",
      fixed: "left",
      width: 40,
      render: (value) => {
        return (
          <Checkbox
            onChange={() => handlePackageArray(value?.id)}
            disabled={selectAll ? true : false}
          ></Checkbox>
        );
      }
    },
    {
      title: "Client",
      width: 80,
      dataIndex: "orderV2",
      render: (value) => {
        return (
          <p style={{ margin: 0, color: "#1f3998", fontSize: "12px" }}>
            {value?.client?.clientName || "Error fetching client name"}
          </p>
        );
      }
    },
    {
      title: "Package ID",
      dataIndex: "id",
      fixed: "left",
      width: 60,
      render: (value) => {
        return (
          <Tooltip placement="top" title="Copy">
            <p
              style={{ margin: 0, color: "#1f3998", fontSize: "12px", cursor: "pointer" }}
              onClick={() => {
                navigator.clipboard.writeText(value);
                messageApi.info("Package ID copied.");
              }}
            >
              {value.substr(0, 8).toUpperCase()}
            </p>
          </Tooltip>
        );
      }
    },
    {
      title: "Order ID",
      width: 60,
      dataIndex: "orderV2_id",
      render: (value) => {
        return (
          <p style={{ margin: 0, color: "#1f3998", fontSize: "12px" }}>
            {value.substr(0, 8).toUpperCase()}
          </p>
        );
      }
    },
    /* Package data is selected and modal shows up when clicking on a package status from the table */
    {
      title: "Status",
      width: 80,
      render: (record) => (
        <PoolStatus
          value={convertEmergencyStatus(record)}
          record={record}
          setSelectedPackage={setSelectedPackage}
          setShowUpdateStatus={setShowUpdateStatus}
          isAdmin={true}
        />
      )
    },
    {
      title: "Factura",
      width: 100,
      render: (record) => {
        const hasFactura = record.facturaId;
        if (!hasFactura) {
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Button
                size="small"
                type="primary"
                style={{ fontSize: "9px", height: "20px", padding: "0 6px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateFactura && onCreateFactura(record);
                }}
              >
                New Factura
              </Button>
              <Button
                size="small"
                style={{ fontSize: "9px", height: "20px", padding: "0 6px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToFactura && onAddToFactura(record);
                }}
              >
                Add to Factura
              </Button>
            </div>
          );
        }
        return <FacturaCell factura={record.factura} isAdmin={true} />;
      }
    },
    {
      title: "Pickup Stop Name",
      width: 110,
      render: (record) => {
        return (
          <span style={{ fontSize: "12px" }}>{record?.pickupStopName || "N/A"}</span>
        );
      }
    },
    {
      title: "Pickup Address",
      width: 120,
      render: (record) => {
        return (
          <div style={{ fontSize: "12px", wordBreak: "break-word" }}>
            {record?.pickupPhysicalAddress ||
              record?.pickupDisplayAddress ||
              record?.pickupAddress ||
              "N/A"}
          </div>
        );
      }
    },
    {
      title: "Dropoff Stop Name",
      width: 110,
      render: (record) => {
        return (
          <span style={{ fontSize: "12px" }}>{record?.dropoffStopName || "N/A"}</span>
        );
      }
    },
    {
      title: "Dropoff Address",
      width: 120,
      render: (record) => {
        return (
          <div style={{ fontSize: "12px", wordBreak: "break-word" }}>
            {record?.dropoffPhysicalAddress ||
              record?.dropoffDisplayAddress ||
              record?.dropoffAddress ||
              "N/A"}
          </div>
        );
      }
    },
    {
      title: "Dropoff Contact Name",
      width: 120,
      render: (record) => {
        return (
          <span style={{ fontSize: "12px" }}>{record?.dropoffContactName || "N/A"}</span>
        );
      }
    },
    {
      title: "Dropoff Phone Number",
      width: 120,
      render: (record) => {
        return (
          <span style={{ fontSize: "12px" }}>{record?.dropoffContactPhone || "N/A"}</span>
        );
      }
    },
    {
      title: "Pickup Date",
      width: 95,
      dataIndex: "pickupDate",
      render: (value) => (
        <span style={{ fontSize: "12px" }}>
          {moment(value).format(DATETIME_FORMAT_FRONTEND)}
        </span>
      )
    },
    {
      title: "Created At",
      width: 95,
      dataIndex: "createdAt",
      render: (value) => (
        <span style={{ fontSize: "12px" }}>
          {moment(value).format(DATETIME_FORMAT_FRONTEND)}
        </span>
      )
    },
    {
      title: "Last Updated At",
      width: 95,
      dataIndex: "updatedAt",
      render: (value) => (
        <span style={{ fontSize: "12px" }}>
          {moment(value).format(DATETIME_FORMAT_FRONTEND)}
        </span>
      )
    }
  ];
  const filteredColumns =
    hiddenColumns.length > 0
      ? columns.filter((col) => !hiddenColumns.includes(col.title))
      : columns;
  return (
    <>
      {contextHolder}
      <TableBase
        columns={filteredColumns}
        data={dataSource}
        pagination={pagination}
        rowKey="id"
        style={{
          overflow: "auto"
        }}
        size="small"
        className="compact-pool-table"
        {...props}
      />
      <style>{`
        .compact-pool-table .ant-table-tbody > tr > td {
          padding: 4px 8px !important;
          font-size: 12px;
        }
        .compact-pool-table .ant-table-thead > tr > th {
          padding: 4px 8px !important;
          font-size: 11px;
          font-weight: 600;
        }
        .compact-pool-table .ant-tag {
          margin: 0 !important;
          padding: 1px 4px !important;
          font-size: 9px !important;
          line-height: 14px !important;
          min-width: 0 !important;
          width: auto !important;
          max-width: 100% !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        .compact-pool-table .ant-table-tbody > tr > td:nth-child(10) {
          max-width: 80px !important;
          overflow: hidden !important;
        }
      `}</style>
      {/* img required as a target for generatePdfPackingSlipPool jsBarcode to render against. It's CSS is set to display none. */}
      <img id="poolPackageBarcode" />
    </>
  );
};
export default FlexioPoolTable;
